import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useRef, useState } from 'react';
import type { GoogleDriveVideoProbeView } from '@tosho/contracts';
import { googleDriveApi, GoogleDriveApiError } from './google-drive-api';

export const videoCheckLabels: Readonly<Record<GoogleDriveVideoProbeView['status'], string>> = {
  PREPARING: 'Préparation de la vidéo de test en cours',
  READY: 'Vidéo de test prête sur Drive · lecture à vérifier',
  NOT_VERIFIED: 'Préparation de la vidéo non vérifiée',
  TRASHED: 'Vidéo de test mise à la corbeille sur Drive',
};

export function DriveVideoProbeCard({
  csrfToken,
  enabled,
  onRefresh,
  onReauthenticate,
}: {
  readonly csrfToken: string;
  readonly enabled: boolean;
  readonly onRefresh: () => Promise<void>;
  readonly onReauthenticate: () => void;
}): React.JSX.Element {
  const [view, setView] = useState<GoogleDriveVideoProbeView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reauth, setReauth] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let active = true;
    void googleDriveApi.readVideoCheck().then(
      (result) => {
        if (active) setView(result);
      },
      () => {
        if (active)
          setError('Le dernier test vidéo ne peut pas être chargé. Rechargez cette page.');
      },
    );
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);
  useEffect(() => {
    if (view?.status !== 'PREPARING' || busy) return;
    let active = true;
    const timer = window.setInterval(() => {
      void googleDriveApi
        .readVideoCheck()
        .then((result) => {
          if (active) setView(result);
        })
        .catch(() => undefined);
    }, 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [view?.status, busy]);

  async function run(cleanup: boolean): Promise<void> {
    if (busy || !enabled) return;
    setOpen(false);
    setBusy(true);
    setError(null);
    setReauth(false);
    try {
      setView(await googleDriveApi.runVideoCheck(csrfToken, cleanup));
    } catch (failure) {
      if (failure instanceof GoogleDriveApiError && failure.code === 'REAUTHENTICATION_REQUIRED') {
        setError('Reconnectez-vous à Tosho puis recommencez.');
        setReauth(true);
      } else if (
        failure instanceof GoogleDriveApiError &&
        failure.code === 'DRIVE_VIDEO_CHECK_BUSY'
      ) {
        setError('Un essai est déjà en cours. Patientez puis rechargez cette page.');
      } else
        setError('Le test n’a pas pu être terminé. Rechargez cette page pour consulter son état.');
      await googleDriveApi
        .readVideoCheck()
        .then(setView)
        .catch(() => undefined);
    } finally {
      await onRefresh().catch(() =>
        setError('L’historique n’a pas pu être actualisé. Rechargez cette page.'),
      );
      setBusy(false);
    }
  }
  return (
    <section
      className="drive-integration__card"
      id="drive-video-check"
      aria-labelledby="drive-video-check-title"
      aria-busy={busy}
    >
      <header>
        <p className="dashboard-panel__eyebrow">Essai de lecture</p>
        <h2 id="drive-video-check-title">Vidéo privée</h2>
      </header>
      <p>
        Tosho dépose une courte vidéo de test sur votre Drive, puis vous permet de la regarder ici,
        sans connexion Google supplémentaire.
      </p>
      <p>
        Aucun fichier client n’est utilisé. La vidéo est lue depuis Drive sans copie temporaire sur
        l’hébergement Tosho. L’essai est réservé à l’administrateur.
      </p>
      <div className="drive-integration__actions">
        <button
          type="button"
          disabled={!enabled || busy || view?.status === 'PREPARING'}
          onClick={() => void run(false)}
        >
          {busy
            ? 'Traitement en cours…'
            : view?.status === 'READY'
              ? 'Revérifier la vidéo de test'
              : 'Préparer la vidéo de test'}
        </button>
        {view?.status === 'READY' ? (
          <button type="button" disabled={!enabled || busy} onClick={() => setOpen(true)}>
            Lire la vidéo de test
          </button>
        ) : null}
        {view && view.status !== 'TRASHED' ? (
          <button
            type="button"
            className="is-secondary"
            disabled={!enabled || busy || view.status === 'PREPARING'}
            onClick={() => void run(true)}
          >
            Ranger la vidéo de test
          </button>
        ) : null}
      </div>
      {error ? (
        <p className="tosho-feedback is-error" role="alert">
          {error}
        </p>
      ) : null}
      {reauth ? (
        <button type="button" onClick={onReauthenticate}>
          Me reconnecter à Tosho
        </button>
      ) : null}
      <p role="status">{view ? videoCheckLabels[view.status] : 'Vidéo non préparée.'}</p>
      <p>
        Après préparation, vérifiez la lecture, le déplacement dans la vidéo et sa fermeture. Le
        bouton « Ranger » place uniquement cette vidéo de test dans la corbeille de Drive.
      </p>
      {open && view && enabled ? (
        <DriveVideoProbePlayer id={view.id} onClose={() => setOpen(false)} />
      ) : null}
    </section>
  );
}

/** Le dialogue natif conserve focus/Échap. Décharger la source annule les requêtes du lecteur
 * même si React démonte le panneau entier. Aucun fetch de fichier complet ou URL Google. */
export function DriveVideoProbePlayer({
  id,
  onClose,
}: {
  readonly id: string;
  readonly onClose: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState(false);
  const safeId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u.test(id);
  const src = safeId ? `/api/integrations/google-drive/video-check/${id}/content` : undefined;
  useEffect(() => {
    dialog.current?.showModal();
    const element = video.current;
    // StrictMode rejoue setup/nettoyage au montage. Réarmer explicitement la source nettoyée
    // évite un lecteur vide au second setup, tout en conservant l'annulation au démontage.
    if (element && src !== undefined) element.setAttribute('src', src);
    return () => {
      if (element) {
        element.pause();
        element.removeAttribute('src');
        element.load();
      }
    };
  }, [src]);
  return (
    <ModalDialog
      ref={dialog}
      className="drive-video-player"
      aria-labelledby="drive-video-player-title"
      onCancel={onClose}
      onClose={onClose}
      close={<ModalCloseButton autoFocus onClick={onClose} />}
    >
      <header className="tosho-modal-header">
        <h2 id="drive-video-player-title">Vidéo de test privée</h2>
      </header>
      {safeId ? (
        <video
          ref={video}
          controls
          playsInline
          preload="metadata"
          src={src}
          onError={() => setError(true)}
        />
      ) : null}
      {error || !safeId ? (
        <p className="tosho-feedback is-error" role="alert">
          La vidéo ne peut pas être lue. Fermez le lecteur puis revérifiez la vidéo de test.
        </p>
      ) : null}
      <p>Mire animée de 8 secondes, sans son. Essayez d’avancer et de reculer dans la vidéo.</p>
    </ModalDialog>
  );
}
